import { Button, DataTable, StatusBadge } from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DuplicateGroup } from '../../schemas/storage.schema';
import { formatBytes } from '../apps/AppList';

function GroupFiles({ g }: { g: DuplicateGroup }) {
  const [open, setOpen] = useState(false);
  const shown = open ? g.files : g.files.slice(0, 2);
  return (
    <div className="space-y-0.5">
      {shown.map((f) => (
        <div key={f.id} className="flex items-center gap-2 text-xs">
          <code className="font-mono">{f.path}</code>
          <span className="text-muted-foreground">
            {new Date(f.createdAt).toLocaleDateString()}
          </span>
        </div>
      ))}
      {g.files.length > 2 ? (
        <Button variant="link" className="h-auto p-0 text-xs" onClick={() => setOpen((o) => !o)}>
          {open ? 'Show less' : `Show all ${g.files.length}`}
        </Button>
      ) : null}
    </div>
  );
}

export function DuplicatesTable({
  rows,
  loading,
  error,
}: {
  rows: DuplicateGroup[];
  loading: boolean;
  error: Error | null;
}) {
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<DuplicateGroup>[]>(
    () => [
      {
        accessorKey: 'appName',
        header: 'App',
        cell: ({ row }) => (
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => navigate(`/files?app=${row.original.appId}`)}
          >
            {row.original.appName}
          </Button>
        ),
      },
      {
        accessorKey: 'tenantId',
        header: 'Tenant',
        cell: ({ row }) =>
          row.original.tenantId ? (
            <code className="font-mono text-xs">{row.original.tenantId}</code>
          ) : (
            <span className="text-xs text-muted-foreground">none</span>
          ),
      },
      {
        accessorKey: 'sha256',
        header: 'sha256',
        cell: ({ row }) => (
          <code className="font-mono text-xs" title={row.original.sha256}>
            {row.original.sha256.slice(0, 12)}…
          </code>
        ),
      },
      {
        accessorKey: 'count',
        header: 'Copies',
        cell: ({ row }) => (
          <StatusBadge tone="warning" dot={false}>
            {row.original.count}×
          </StatusBadge>
        ),
      },
      {
        id: 'size',
        header: 'Size',
        cell: ({ row }) => <span className="text-sm">{formatBytes(row.original.size)}</span>,
      },
      {
        id: 'recoverable',
        header: 'Recoverable',
        cell: ({ row }) => (
          <span className="text-sm font-medium">{formatBytes(row.original.recoverableBytes)}</span>
        ),
      },
      { id: 'files', header: 'Files', cell: ({ row }) => <GroupFiles g={row.original} /> },
    ],
    [navigate],
  );
  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={loading}
      error={error}
      hidePagination
      aria-label="Duplicate groups"
      empty={
        <p className="py-6 text-center text-sm text-muted-foreground">
          No duplicates: every hash appears once per app and tenant
        </p>
      }
    />
  );
}
